import React from "react";
import { withRouter } from "react-router";
import { Container } from "../../components/container";
import ServiceFactory from "../../service";

/*
 * Meta OAuth callback — the landing page for the "Connect with Facebook" flow
 * (SOCIAL_STATS.md P2). Meta redirects the top window here with ?code=…&state=…
 * after the admin logs in and grants access. We:
 *   1. POST the code to /v1/social/oauth/meta/complete → the server exchanges it
 *      for a long-lived user token (kept server-side) and returns the Pages the
 *      admin manages plus each Page's connected Instagram account.
 *   2. Let the admin pick which Page (+ its Instagram) to connect.
 *   3. POST the choice to /v1/social/oauth/meta/finish → the server stores the
 *      Page token and creates the social_account rows. No token ever reaches the
 *      browser.
 * redirect_uri is `${origin}/OAuth/Meta` — that exact URL must be whitelisted in
 * the Meta app dashboard.
 */

function svc(api) {
  const Service = ServiceFactory.getType(api, "product"); // /v1 root
  return new Service();
}
class MetaOAuthCallback extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      phase: "exchanging",
      error: null,
      pages: null,
      chosen: null,
      busy: false,
      connected: null,
      permissions: [],
      requested: []
    };
  }
  componentDidMount() {
    const q = new URLSearchParams(window.location.search);
    const err = q.get("error_description") || q.get("error");
    const code = q.get("code");
    if (err) {
      this.setState({
        phase: "error",
        error: this.friendly(err)
      });
      return;
    }
    if (!code) {
      this.setState({
        phase: "error",
        error: "No authorization code returned from Meta."
      });
      return;
    }
    this.complete(code);
  }
  friendly(e) {
    if (/denied|access_denied/i.test(e)) return "Access was not granted. To connect, approve the requested permissions in the Meta dialog.";
    return String(e);
  }
  redirectUri() {
    return window.location.origin + "/OAuth/Meta";
  }
  async complete(code) {
    try {
      const r = await svc("/social/oauth/meta/complete").postAsync({
        code,
        redirectUri: this.redirectUri()
      });
      const pages = r && r.pages || [];
      if (!pages.length) {
        this.setState({
          phase: "error",
          error: "No Facebook Pages were found on this account. Connect an account that manages a Page with a linked Instagram business profile."
        });
        return;
      }
      this.setState({
        phase: "pick",
        pages,
        chosen: pages[0].pageId,
        permissions: r && r.permissions || [],
        requested: r && r.requested || []
      });
    } catch (e) {
      this.setState({
        phase: "error",
        error: e && e.message && e.message.length < 200 ? e.message : "Could not complete the connection with Meta."
      });
    }
  }
  async finish() {
    this.setState({
      busy: true,
      error: null
    });
    try {
      const r = await svc("/social/oauth/meta/finish").postAsync({
        pageId: this.state.chosen
      });
      this.setState({
        busy: false,
        phase: "done",
        connected: r && r.connected || []
      });
    } catch (e) {
      this.setState({
        busy: false,
        error: e && e.message && e.message.length < 200 ? e.message : "Could not save the connection."
      });
    }
  }

  // What Meta granted, declined, or never asked for — the publishing permissions matter for Content Hub › Ship.
  renderPermissions() {
    const KEY = {
      pages_manage_posts: "Post to your Page (pages_manage_posts)",
      instagram_content_publish: "Post to Instagram (instagram_content_publish)"
    };
    const got = {};
    (this.state.permissions || []).forEach(p => {
      got[String(p.permission)] = String(p.status);
    });
    const rows = Object.keys(KEY).map(k => ({
      k,
      label: KEY[k],
      status: got[k] || "not requested"
    }));
    const missing = rows.filter(r => r.status !== "granted");
    return /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: 12,
        fontSize: 12.5
      }
    }, /*#__PURE__*/React.createElement("div", {
      style: {
        fontWeight: 600,
        marginBottom: 4
      }
    }, "Publishing permissions"), rows.map(r => /*#__PURE__*/React.createElement("div", {
      key: r.k,
      style: {
        color: r.status === "granted" ? "#1d7a3e" : "#8a4b09"
      }
    }, r.status === "granted" ? "✓" : "✕", " ", r.label, " \u2014 ", r.status)), missing.length > 0 && /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: "6px 0 0",
        textAlign: "left"
      }
    }, missing.some(r => r.status === "declined") ? "A permission was declined in the Meta dialog. Reconnect and switch it on when Meta asks." : "Meta never asked for it, so the Facebook Login configuration on the Plunk Social app does not include that permission yet. Add it there, then reconnect.", " ", "Reads and insights still work either way."));
  }
  render() {
    const {
      phase,
      error,
      pages,
      chosen,
      busy,
      connected
    } = this.state;
    return /*#__PURE__*/React.createElement(Container, {
      title: "Connect Meta",
      css: "margin-squeeze"
    }, phase === "exchanging" && /*#__PURE__*/React.createElement("p", {
      className: "territory-hint"
    }, "Finishing the connection with Meta\u2026"), phase === "error" && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("p", {
      style: {
        color: "#b3261e",
        maxWidth: "70ch"
      }
    }, error), /*#__PURE__*/React.createElement("button", {
      className: "territory-toggle",
      onClick: () => this.props.history.push("/Data/Connect")
    }, "Back to Connections")), phase === "pick" && /*#__PURE__*/React.createElement("div", {
      style: {
        maxWidth: 520,
        textAlign: "left"
      }
    }, /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: "0 0 12px",
        textAlign: "left"
      }
    }, "Choose the Facebook Page to connect. Its linked Instagram account connects with it."), /*#__PURE__*/React.createElement("div", {
      style: {
        border: "1px solid var(--plk-border, #e4e7ec)",
        borderRadius: 8,
        overflow: "hidden"
      }
    }, pages.map((p, i) => {
      const sel = chosen === p.pageId;
      return /*#__PURE__*/React.createElement("div", {
        key: p.pageId,
        role: "button",
        onClick: () => this.setState({
          chosen: p.pageId
        }),
        style: {
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "flex-start",
          gap: 12,
          textAlign: "left",
          padding: "12px 14px",
          cursor: "pointer",
          borderTop: i === 0 ? "none" : "1px solid var(--plk-border, #eef0f3)",
          background: sel ? "var(--plk-surface-alt, #f3f7ff)" : "transparent"
        }
      }, /*#__PURE__*/React.createElement("input", {
        type: "radio",
        name: "page",
        checked: sel,
        readOnly: true,
        style: {
          margin: "3px 0 0",
          flex: "0 0 auto",
          width: 16,
          height: 16
        }
      }), /*#__PURE__*/React.createElement("div", {
        style: {
          flex: "1 1 auto",
          minWidth: 0,
          lineHeight: 1.4
        }
      }, /*#__PURE__*/React.createElement("div", {
        style: {
          fontWeight: 600
        }
      }, p.pageName), /*#__PURE__*/React.createElement("div", {
        className: "territory-hint",
        style: {
          margin: 0
        }
      }, p.hasInstagram ? `Instagram @${p.igUsername}` : "No linked Instagram")));
    })), this.renderPermissions(), error && /*#__PURE__*/React.createElement("p", {
      style: {
        color: "#b3261e"
      }
    }, error), /*#__PURE__*/React.createElement("div", {
      style: {
        marginTop: 14,
        display: "flex",
        gap: 8
      }
    }, /*#__PURE__*/React.createElement("button", {
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary",
      disabled: busy || !chosen,
      onClick: () => this.finish()
    }, busy ? "Connecting…" : "Connect"), /*#__PURE__*/React.createElement("button", {
      className: "territory-toggle",
      onClick: () => this.props.history.push("/Data/Connect")
    }, "Cancel"))), phase === "done" && /*#__PURE__*/React.createElement("div", {
      style: {
        maxWidth: "60ch"
      }
    }, /*#__PURE__*/React.createElement("p", {
      style: {
        color: "#1d7a3e"
      }
    }, "Connected ", (connected || []).map(c => `${c.platform === "instagram" ? "Instagram" : "Facebook"} (${c.handle || c.platform})`).join(" + "), ". Insights import on the next sync and appear under Social within a day."), /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: 8
      }
    }, /*#__PURE__*/React.createElement("button", {
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary",
      onClick: () => this.props.history.push("/Social")
    }, "Go to Social"), /*#__PURE__*/React.createElement("button", {
      className: "territory-toggle",
      onClick: () => this.props.history.push("/Data/Connect")
    }, "Connections"))));
  }
}
const Routable = withRouter(MetaOAuthCallback);
export class MetaOAuthCallbackViewComponent extends React.Component {
  render() {
    return /*#__PURE__*/React.createElement(Routable, this.props);
  }
}
export default {
  MetaOAuthCallbackViewComponent
};